import { useEffect, useRef, useCallback } from "react";

/**
 * Melhoria 11 — rascunho automático de UM campo de texto (comentário, ajuste).
 * Persiste no localStorage enquanto a pessoa escreve, restaura sozinho quando a
 * tela reabre (ou troca de card / recarrega / o backend cai) e limpa no envio.
 * Silencioso: não pergunta "restaurar?", só devolve o texto pro campo.
 *
 * Cuidado tratado: o drawer NÃO remonta ao trocar de card (só muda o tarefaId),
 * então a `chave` muda em runtime. O guard `prevChave` evita gravar o texto do
 * card ANTERIOR na chave do card NOVO no exato tick da troca.
 *
 * @param chave  chave de storage já namespaced (ex.: `rascunho_comentario_<id>`)
 * @param valor  o texto atual (markup) do campo
 * @param setValor  setter do campo (usado pra restaurar)
 */
export function useRascunhoCampo(chave: string, valor: string, setValor: (v: string) => void) {
  const prevChave = useRef<string | null>(null);

  // Restaura ao montar e sempre que a chave muda (troca de card).
  useEffect(() => {
    let salvo = "";
    try { salvo = localStorage.getItem(chave) || ""; } catch { /* ignora */ }
    setValor(salvo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);

  // Salva a cada mudança do texto. Pula o tick da troca de chave (o `valor`
  // ainda é o do card anterior nesse instante).
  useEffect(() => {
    if (prevChave.current !== chave) { prevChave.current = chave; return; }
    try {
      if (valor && valor.trim()) localStorage.setItem(chave, valor);
      else localStorage.removeItem(chave);
    } catch { /* ignora */ }
  }, [valor, chave]);

  return useCallback(() => {
    try { localStorage.removeItem(chave); } catch { /* ignora */ }
  }, [chave]);
}
